<template>
  <main class="access-management-page">
    <!-- Header Card -->
    <PageHeader icon="fas fa-users-cog">
      <template #title>
        Hak Akses Khusus Pengguna
      </template>
      <template #subtitle>
        Gunakan hanya untuk pengecualian izin langsung. Role dan akses cabang dikelola dari Pengguna & Cabang.
      </template>
      <template #actions>
        <b-button variant="primary" @click="$router.push('/user-managements/security-center')">
          <i class="fas fa-users mr-2"></i>Pengguna & Cabang
        </b-button>
      </template>
    </PageHeader>

    <div class="access-management-grid">
      <section class="access-management-panel access-management-panel--subjects">
        <div class="access-management-panel__heading">
          <div>
            <span class="access-management-eyebrow">Daftar pengguna</span>
            <h2>Pilih pengguna</h2>
            <p>{{ totalRows }} pengguna tersedia</p>
          </div>
          <span class="access-management-panel__icon"><i class="fas fa-user-md"></i></span>
        </div>

        <div class="access-management-tools access-management-tools--stacked">
          <b-input-group class="access-management-search">
            <template #prepend><b-input-group-text><b-icon-search /></b-input-group-text></template>
            <b-form-input
              v-model="filter.name"
              type="text"
              placeholder="Cari nama pengguna"
              aria-label="Cari nama pengguna"
              @keyup="filterByName" />
          </b-input-group>
          <b-tabs v-model="tabPosition" class="access-management-tabs">
            <b-tab title="Dokter" active @click="changeTable(0)" />
            <b-tab title="Karyawan" @click="changeTable(1)" />
          </b-tabs>
        </div>

        <div class="access-management-table access-management-table--subjects">
          <b-table hover :items="itemsDoctor" :fields="fieldsDoctor" responsive show-empty empty-text="Belum ada data pengguna">
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.key === 'actions' ? '92px' : field.key === 'number' ? '58px' : '' }" />
            </template>
            <template #cell(name)="data">
              <div class="access-management-subject">
                <span class="access-management-avatar"><i :class="doctorTable ? 'fas fa-user-md' : 'fas fa-user-tie'"></i></span>
                <span>{{ data.value }}</span>
              </div>
            </template>
            <template #cell(actions)="data">
              <button
                type="button"
                class="access-management-select"
                :class="{ 'is-selected': user_id == data.item.user_id }"
                @click="getAccess(data.item.user_id)">
                <i :class="user_id == data.item.user_id ? 'fas fa-check' : 'fas fa-arrow-right'"></i>
                {{ user_id == data.item.user_id ? 'Dipilih' : 'Pilih' }}
              </button>
            </template>
          </b-table>
        </div>

        <footer v-if="itemsDoctor.length != 0" class="access-management-footer">
          <span>{{ itemsDoctor.length }} dari {{ totalRows }} data</span>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @page-click="pageOnClickUser" />
        </footer>
      </section>

      <section class="access-management-panel access-management-panel--permissions">
        <div class="access-management-panel__heading access-management-panel__heading--permission">
          <div>
            <span class="access-management-eyebrow">Konfigurasi izin</span>
            <h2>Hak akses sistem</h2>
            <p v-if="username">Pengguna terpilih: <strong>{{ username }}</strong></p>
            <p v-else>Pilih pengguna untuk menampilkan daftar izin.</p>
          </div>
          <span class="access-management-panel__icon access-management-panel__icon--teal"><i class="fas fa-key"></i></span>
        </div>

        <div v-if="username" class="access-management-tools access-management-tools--permission">
          <b-input-group class="access-management-search">
            <template #prepend><b-input-group-text><b-icon-search /></b-input-group-text></template>
            <b-form-input
              id="user-filter-input"
              v-model="filterAccess"
              type="search"
              placeholder="Cari fitur atau modul"
              aria-label="Cari hak akses" />
          </b-input-group>
          <span class="access-management-legend"><i class="fas fa-lock"></i> Warisan jabatan / role</span>
        </div>
        <div v-if="username" class="access-catalog-summary">
          <span><strong>{{ permissionStats.modules }}</strong> modul aktif</span>
          <span><strong>{{ permissionStats.permissions }}</strong> hak akses</span>
          <span><strong>{{ permissionStats.active }}</strong> diberikan</span>
        </div>

        <div v-if="!username" class="access-management-empty">
          <span><i class="fas fa-hand-pointer"></i></span>
          <h3>Belum ada pengguna dipilih</h3>
          <p>Pilih dokter atau karyawan dari panel kiri untuk mengatur aksesnya.</p>
        </div>

        <div v-else class="access-management-table access-management-table--permissions">
          <b-table
            id="user-access-table"
            hover
            :items="access_lists_display"
            :fields="fieldAccess"
            :filter="filterAccess"
            :tbody-tr-class="accessRowClass"
            responsive>
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.key === 'actions' ? '82px' : field.key === 'code' ? '120px' : '' }" />
            </template>
            <template #cell(code)="data">
              <span v-if="data.item.row_type === 'section'" class="access-management-section-title">{{ data.item.name }}</span>
              <span v-else-if="data.item.row_type === 'module'" class="access-management-module-title"><i :class="data.item.module_icon"></i>{{ data.item.name }}</span>
              <span v-else class="access-management-code">{{ data.item.code }}</span>
            </template>
            <template #cell(name)="data">
              <span v-if="data.item.row_type === 'permission'" class="permission-name"><span class="permission-type" :class="'is-' + data.item.permission_type">{{ permissionTypeLabel(data.item.permission_type) }}</span>{{ data.item.name }}</span>
            </template>
            <template #cell(actions)="data">
              <button
                v-if="data.item.row_type === 'permission'"
                type="button"
                class="access-management-toggle"
                :class="data.item.access_status_position ? 'is-inherited' : data.item.access_status ? 'is-active' : 'is-inactive'"
                :disabled="data.item.access_status_position == true"
                :aria-label="data.item.access_status_position ? 'Akses diwariskan dari jabatan atau role' : data.item.access_status ? 'Nonaktifkan akses' : 'Aktifkan akses'"
                @click="toggleAccess(data.item)">
                <i :class="data.item.access_status_position ? 'fas fa-lock' : data.item.access_status ? 'fas fa-check' : 'fas fa-times'"></i>
              </button>
            </template>
          </b-table>
        </div>
        <div v-if="username && accessDraftDirty" class="access-save-bar"><div><strong>{{ pendingChangeCount }} perubahan belum disimpan</strong><small>Perubahan baru berlaku setelah ditinjau dan disimpan.</small></div><b-form-input v-model="accessChangeReason" placeholder="Alasan perubahan (minimal 5 karakter)" /><b-button variant="outline-secondary" @click="cancelAccessDraft">Batal</b-button><b-button variant="primary" @click="previewAccessDraft">Tinjau & simpan</b-button></div>
      </section>
    </div>
    <b-modal id="access-preview-modal" title="Tinjau Perubahan Hak Akses" @ok.prevent="applyAccessDraft" ok-title="Terapkan perubahan" cancel-title="Kembali"><div v-if="accessPreview"><b-alert :show="accessPreview.conflicts&&accessPreview.conflicts.length>0" variant="warning"><strong>{{ accessPreview.conflicts.length }} konflik pemisahan tugas.</strong> Pastikan perubahan ini memang dibutuhkan.</b-alert><div class="preview-columns"><section><h6>Ditambahkan ({{ accessPreview.added.length }})</h6><span v-for="code in accessPreview.added" :key="'a'+code">+ {{ code }}</span><em v-if="!accessPreview.added.length">Tidak ada</em></section><section><h6>Dicabut ({{ accessPreview.removed.length }})</h6><span v-for="code in accessPreview.removed" :key="'r'+code">− {{ code }}</span><em v-if="!accessPreview.removed.length">Tidak ada</em></section></div><p class="mt-3 mb-0"><strong>Alasan:</strong> {{ accessChangeReason }}</p></div></b-modal>
  </main>
</template>

<script>

import { debouncer } from "@/core/modules/Helper.js"
import module from "@/core/modules/CrudModule.js";
import { buildPermissionRows, permissionSummary } from "@/core/modules/AccessCatalog.js";
import api from "@/core/services/api.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { getUser, setUser } from '@/core/services/jwt.service.js'

export default {
  name: "List",

  data() {
    return {
      // Filter
      filter: {
        name: "",
      },
      filterAccess: "",
      // display left table
      selected: 1,
      tabPosition: 0,
      doctorTable: true,
      employeeTable: false,
      options: [
        { value: 1, text: "Daftar Dokter" },
        { value: 2, text: "Daftar Karyawan" },
      ],
      // Pagination
      perPage: 20,
      currentPage: 1,
      totalRows: 0,
      perPageAccess: 10,
      currentPageAccess: 1,
      totalRowsAccess: 1,
      // Table Head
      fieldsDoctor: [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "name",
          label: "Nama",
          sortable: true,
        },
        {
          key: "polyclinic_name",
          label: "Poliklinik",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ],
      fieldsEmployee: [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "name",
          label: "Nama",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ],
      fieldAccess: [
        {
          key: "code",
          label: "Kode",
          sortable: true,
        },
        {
          key: "name",
          label: "Fitur / Modul Sistem",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ],
      // Table Items Doctor
      itemsDoctor: [],
      // Table Items Employee
      itemsEmployee: [],
      // Table Items Access List
      access_lists: [],
      access_lists_children: [],
      access_lists_display: [],
      //
      access_by_user: [],
      originalAccessCodes: [],
      pendingAccessCodes: [],
      accessChangeReason: "",
      accessPreview: null,
      // user name
      username: "",
      user_id: "",
      clinic_name: window.configuration == null ? "Medhop" : window.configuration.clinic_name
    };
  },

  computed: {
    accessDraftDirty() {
      return JSON.stringify([...this.originalAccessCodes].sort()) !== JSON.stringify([...this.pendingAccessCodes].sort());
    },
    pendingChangeCount() {
      const added = this.pendingAccessCodes.filter(code => !this.originalAccessCodes.includes(code));
      const removed = this.originalAccessCodes.filter(code => !this.pendingAccessCodes.includes(code));
      return added.length + removed.length;
    },
    permissionStats() {
      return permissionSummary(this.access_lists_display);
    }
  },

  methods: {
    accessRowClass(item) {
      if (!item) return "";
      if (item.row_type === "section") return "access-management-section-row";
      if (item.row_type === "module") return "access-management-module-row";
      return "access-management-permission-row";
    },
    permissionTypeLabel(type) {
      return { view: "Lihat", manage: "Kelola", approval: "Persetujuan" }[type] || "Akses";
    },

    async pageOnClickUser(evt, page) {
      evt.preventDefault();
      this.currentPage = page;
      await this.pagination(this.employeeTable ? "employees" : "doctors");
    },
    changeTable(id) {
      if (id == 0) {
        this.pagination("doctors");
        this.setFieldDoctor();
        this.employeeTable = false;
        this.doctorTable = true;
      } else if (id == 1) {
        this.pagination("employees");
        this.setFieldEmployee();
        this.doctorTable = false;
        this.employeeTable = true;
      }
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRowsAccess = filteredItems.length;
      this.currentPage = 1;
    },

    resetArrayOnNew() {
      this.access_lists = [];
      this.access_lists_children = [];
      this.access_lists_display = [];
      this.access_by_user = [];
      this.originalAccessCodes = [];
      this.pendingAccessCodes = [];
      this.accessChangeReason = "";
    },

    setFieldDoctor() {
      this.fieldsDoctor = [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "name",
          label: "Nama",
          sortable: true,
        },
        {
          key: "polyclinic_name",
          label: "Poliklinik",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ];
    },

    setFieldEmployee() {
      this.fieldsDoctor = [
        {
          key: "number",
          label: "#",
          sortable: true,
        },
        {
          key: "name",
          label: "Nama",
          sortable: true,
        },
        {
          key: "position_name",
          label: "Jabatan",
          sortable: true,
        },
        {
          key: "department_name",
          label: "Departemen",
          sortable: true,
        },
        { key: "actions", label: "Aksi" },
      ];
    },

    // function tabel dokter di sebelah kiri
    async pageOnClickDoctor(evt, page) {
      evt.preventDefault();
      this.currentPage = page;
      await this.paginationDoctor();
    },

    async pagination(route = "doctors") {
      let filterParams = `&name=${this.filter.name}`;
      let response = await module.paginate(
        route,
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      let pagination = response.meta.pagination;
      this.itemsDoctor = response.data;
      this.totalRows = pagination.total;
    },

    async paginationDoctor() {
      let filterParams = `&name=${this.filter.name}`;
      let response = await module.paginate(
        "doctors",
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      let pagination = response.meta.pagination;
      this.itemsDoctor = response.data;
      this.totalRows = pagination.total;
    },

    async allAccess() {
      let response = await module.paginate(
        "access-lists",
      );
    },

    // function tabel karyawan di sebelah kiri
    async pageOnClickEmployee(evt, page) {
      evt.preventDefault();
      this.currentPage = page;
      await this.paginationEmployee();
    },

    async paginationEmployee() {
      let filterParams = `&name=${this.filter.name}`;
      let response = await module.paginate(
        "employees",
        `?page=${this.currentPage}&page_size=${this.perPage}${filterParams}`
      );
      let pagination = response.meta.pagination;
      this.itemsEmployee = response.data;
      this.totalRows = pagination.total;
    },

    filterByName() {
      debouncer(() => {
        if (this.doctorTable == true) {
          this.pagination("doctors");
        } else if (this.employeeTable == true) {
          this.pagination("employees");
        }
      }, 500)
    },

    // function tabel hak akses sebelah kanan
    async getAccessList() {
      let response = await module.paginate(
        "access-lists"
      );
      this.access_lists = response.data;

      this.access_lists_display = [];
      let a, b, c;
      for (a = 0; a < this.access_lists.length; a++) {
        this.access_lists_children = response.data[a].children;
        this.access_lists_display.push(this.access_lists[a]);
        for (b = 0; b < this.access_lists_children.length; b++) {
          this.access_lists_display.push(this.access_lists_children[b]);
          for (c = 0; c < this.access_lists_display.length; c++) {
            if (this.access_lists_display[c].parent_id !== 0) {
              this.access_lists_display[c].number = "-";
            }
          }
        }
      }
      let pagination = response.meta.pagination;
      // this.totalRows = pagination.total;
    },

    getAccess(id) {
      this.resetArrayOnNew()
      this.getAccessByUser(id)
    },

    async getAccessByUser(id) {
      // this.resetArrayOnNew();

      // get access by user
      let access_by_user = await module.getAccessByUser("users/" + id);
      this.username = access_by_user.data.name;
      if (access_by_user.data.polyclinic_name) {
        this.username = access_by_user.data.name + " - " + access_by_user.data.polyclinic_name;
      }
      this.user_id = access_by_user.data.user_id;
      const effectiveResponse = await api.get("access-governance/users/" + id + "/effective-access");
      const sourceData = effectiveResponse.data.data || {};
      let access_right = (sourceData.sources.direct || []).map(String);
      const inheritedAccess = (sourceData.inherited_codes || [
        ...(sourceData.sources.position || []),
        ...(sourceData.sources.role || []),
      ]).map(String);

      // Position is kept in the identity label; inherited permissions come
      // from the canonical effective-access endpoint (position + roles).
      if (access_by_user.data.position_id) {
        this.username = access_by_user.data.name + " - " + access_by_user.data.position_name + " - " + access_by_user.data.department_name
      }

      const response = await api.get("access-governance/permissions");
      this.access_lists = response.data.data || [];
      this.access_lists_display = buildPermissionRows(this.access_lists, access_right, inheritedAccess);
      this.originalAccessCodes = this.access_lists_display.filter(item => item.row_type === "permission" && item.access_status && !item.access_status_position).map(item => String(item.code));
      this.pendingAccessCodes = [...this.originalAccessCodes];
      this.accessChangeReason = "";
    },

    toggleAccess(item) {
      if (item.access_status_position) return;
      item.access_status = !item.access_status;
      this.pendingAccessCodes = this.access_lists_display.filter(row => row.row_type === "permission" && row.access_status && !row.access_status_position).map(row => String(row.code));
    },

    cancelAccessDraft() {
      this.access_lists_display.forEach(item => {
        if (item.row_type === "permission" && !item.access_status_position) item.access_status = this.originalAccessCodes.includes(String(item.code));
      });
      this.pendingAccessCodes = [...this.originalAccessCodes];
      this.accessChangeReason = "";
    },

    async previewAccessDraft() {
      if (this.accessChangeReason.trim().length < 5) {
        this.$bvToast.toast("Isi alasan perubahan minimal 5 karakter.", { title: "Alasan diperlukan", variant: "warning", solid: true });
        return;
      }
      const response = await api.post("access-governance/users/" + this.user_id + "/access/preview", { access_codes: this.pendingAccessCodes });
      this.accessPreview = response.data.data;
      this.$bvModal.show("access-preview-modal");
    },

    async applyAccessDraft() {
      const response=await api.update("access-governance/users/" + this.user_id, "access", { access_codes: this.pendingAccessCodes, reason: this.accessChangeReason, acknowledge_conflicts: true });
      this.$bvModal.hide("access-preview-modal");
      this.$bvToast.toast(response.data.message || "Hak akses pengguna berhasil diperbarui.", { title: response.data.status === "pending_approval" ? "Menunggu persetujuan" : "Berhasil", variant: response.data.status === "pending_approval" ? "warning" : "success", solid: true });
      await this.getAccessByUser(this.user_id);
    },

    async attachUser(id) {
      let targetUser = this.user_id
      let done = await api
        .post("access-lists/" + id + "/" + this.user_id + "/attach/user")
        .then((response) => {
          this.getAccessByUser(this.user_id);
          // if (targetUser == getUser().user_id) {
          //   localStorage.setItem('access_right', response.data.access_right)
          //   localStorage.setItem('access_right_display', JSON.stringify(response.data.access_right_display))
          //   return true
          // }
        });
      if (done) {
        // this.$root.$emit('setMenuActive');
        // this.$root.$emit('UpdateSideMenu');
      }

      //  await api
      //   .post("access-lists/" + id + "/" + this.user_id + "/attach/user")
      //   .then((response) => {
      //     console.log("attach user", response);
      //     this.getAccessByUser(this.user_id);
      //     this.$root.$emit("setMenuActive");
      //   });
    },

    async detachUser(id) {
      let targetUser = this.user_id
      let done = await api
        .delete("access-lists/" + id + "/" + this.user_id + "/detach/user")
        .then((response) => {
          this.getAccessByUser(this.user_id);
          // if (targetUser == getUser().user_id) {
          //   localStorage.setItem('access_right', response.data.access_right)
          //   localStorage.setItem('access_right_display', JSON.stringify(response.data.access_right_display))
          //   return true
          // }
        });
      if (done) {
        // this.$root.$emit('setMenuActive');
        // this.$root.$emit('UpdateSideMenu');
      }

      //  await api
      //   .delete("access-lists/" + id + "/" + this.user_id + "/detach/user")
      //   .then((response) => {
      //     console.log("detach user", response);
      //     this.getAccessByUser(this.user_id);
      //     this.$root.$emit("setMenuActive");
      //   });
    },

    async deleteData(id) {
      // Delete Data
      let result = await module.delete("action-types/" + id);
      // If Deleted
      if (result) {
      }
    },
  },

  mounted() {
    // BreadCrumb
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Hak Akses User" }]);
    // Get Data
    // this.getAccessList();
    this.paginationDoctor();
    this.allAccess()
  },
};
</script>

<style lang="scss" src="@/assets/sass/pages/_access-management.scss"></style>
<style scoped>
.access-save-bar{position:sticky;bottom:1rem;z-index:5;display:grid;grid-template-columns:1.2fr 1.6fr auto auto;align-items:center;gap:.65rem;margin-top:1rem;padding:.85rem;background:#fff;border:1px solid #cddcf5;border-radius:12px;box-shadow:0 14px 35px rgba(33,61,100,.15)}.access-save-bar small{display:block;color:#718096}.preview-columns{display:grid;grid-template-columns:1fr 1fr;gap:1rem}.preview-columns section{padding:1rem;border:1px solid #e7edf4;border-radius:10px}.preview-columns span,.preview-columns em{display:block;padding:.2rem 0}@media(max-width:768px){.access-save-bar{grid-template-columns:1fr}.preview-columns{grid-template-columns:1fr}}
</style>
